import {
  CheckoutButtonInitializer,
  InitializeButtonProps,
  SmartPaymentButtonInitializationProps,
} from '../../types';

export default class SmartPaymentButtonConstructor {
  private checkoutButtonInitializer?: CheckoutButtonInitializer;
  private uid: string = new Date().getTime().toString(16);

  async loadCheckoutButtonInitializer(host: string): Promise<void> {
    if (!this.checkoutButtonInitializer) {
      const checkoutKitModule = await window.checkoutKitLoader?.load('checkout-button');

      if (!checkoutKitModule) {
        throw new Error(
          'Can not initialize smart payment button because checkoutKitModule is not loaded',
        );
      }

      this.checkoutButtonInitializer = checkoutKitModule.createCheckoutButtonInitializer({ host });
    }
  }

  createButtonContainer(methodId: string): HTMLElement {
    const buttonContainerId = this.getButtonContainerId(methodId);
    const buttonContainer = document.createElement('div');

    buttonContainer.setAttribute('data-smart-button-container-id', buttonContainerId);

    return buttonContainer;
  }

  initializeButton(smartPaymentButtonProps: SmartPaymentButtonInitializationProps): void {
    if (!this.checkoutButtonInitializer) {
      throw new Error(
        'Can not initialize smart payment button because checkoutButtonInitializer is not loaded',
      );
    }

    const buttonOptions = this.getButtonInitializationOptions(smartPaymentButtonProps);

    this.checkoutButtonInitializer.initializeButton(buttonOptions);
  }

  getButtonContainerId(methodId: string): string {
    return `bc-smart-payment-button-${methodId}_${this.uid}`;
  }

  getButtonInitializationOptions(
    smartPaymentButtonProps: SmartPaymentButtonInitializationProps,
  ): InitializeButtonProps {
    const {
      buyNowInitializeOptions,
      currencyCode,
      gatewayId,
      initializationOptions,
      methodId,
      confirmationPageRedirectUrl,
      requiresShipping,
      isWebBrowserSupported,
    } = smartPaymentButtonProps;

    const buttonContainerId = this.getButtonContainerId(methodId);

    let customProviderOptions = {};

    if (
      gatewayId === 'paypalcommerce' ||
      gatewayId === 'paypalcommercecredit' ||
      gatewayId === 'paypalcommercevenmo' ||
      gatewayId === 'paypalcommercealternativemethods' ||
      gatewayId === 'bigcommerce_payments_paypal' ||
      gatewayId === 'bigcommerce_payments_paylater' ||
      gatewayId === 'bigcommerce_payments_venmo' ||
      gatewayId === 'bigcommerce_payments_apms'
    ) {
      customProviderOptions = {
        ...customProviderOptions,
        initializesOnCheckoutPage: false,
      };
    }

    if (
      gatewayId === 'paypalcommerce' ||
      gatewayId === 'paypalcommercecredit' ||
      gatewayId === 'bigcommerce_payments_paypal' ||
      gatewayId === 'bigcommerce_payments_paylater'
    ) {
      customProviderOptions = {
        ...customProviderOptions,
        ...(confirmationPageRedirectUrl && {
          onComplete: () => {
            window.location.replace(confirmationPageRedirectUrl);
          },
        }),
      };
    }

    if (gatewayId === 'applepay') {
      customProviderOptions = {
        ...customProviderOptions,
        ...(confirmationPageRedirectUrl && {
          onPaymentAuthorize: () => {
            window.location.replace(confirmationPageRedirectUrl);
          },
        }),
        isWebBrowserSupported,
        requiresShipping,
      };
    }

    if (
      gatewayId === 'paypalcommercealternativemethods' ||
      gatewayId === 'bigcommerce_payments_apms'
    ) {
      customProviderOptions = {
        ...customProviderOptions,
        apm: methodId,
      };
    }

    if (gatewayId === 'amazonpay') {
      customProviderOptions = {
        ...customProviderOptions,
        buttonColor: initializationOptions?.style?.color,
      };
    }

    if (methodId === 'googlepay') {
      customProviderOptions = {
        ...customProviderOptions,
        buttonColor: initializationOptions?.style?.color,
      };
    }

    return {
      containerId: `[data-smart-button-container-id=${buttonContainerId}]`,
      methodId: gatewayId,
      currencyCode,
      [gatewayId]: {
        buyNowInitializeOptions,
        currencyCode,
        ...initializationOptions,
        ...customProviderOptions,
      },
    };
  }
}
